import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { ALL_APPS } from '../constants/apps';
import { useAuth } from '../contexts/AuthContext';
import { supabasePermissionsService } from '../services/supabase-permissions.service';
export function AppSwitcher() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [userAppAccess, setUserAppAccess] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [unsubscribe, setUnsubscribe] = useState(null);
    // Load user's app access and subscribe to changes
    useEffect(() => {
        if (!user?.id) {
            setLoading(false);
            return;
        }
        const loadAppAccess = async () => {
            try {
                const appIds = await supabasePermissionsService.getUserBlueAppAccess(user.id);
                setUserAppAccess(new Set(appIds));
            }
            catch (error) {
                console.error('Failed to load user app access:', error);
            }
            finally {
                setLoading(false);
            }
        };
        // Initial load
        loadAppAccess();
        // Subscribe to real-time changes
        const unsubFunc = supabasePermissionsService.subscribeToBlueAppAccessChanges(user.id, (appIds) => {
            console.log('App access updated:', appIds);
            setUserAppAccess(new Set(appIds));
        });
        setUnsubscribe(unsubFunc);
        // Cleanup subscription on unmount
        return () => {
            if (unsubFunc) {
                unsubFunc();
            }
        };
    }, [user?.id]);
    const handleAppClick = (app) => {
        // App is available if user has access to it
        if (userAppAccess.has(app.id)) {
            navigate(app.path);
        }
    };
    return (_jsxs("div", { className: "flex flex-col h-full", style: { backgroundColor: 'var(--bg-base)' }, children: [_jsxs("div", { className: "flex items-center justify-between px-8 py-6", style: { borderBottom: '1px solid var(--border-color)' }, children: [_jsxs("div", { children: [_jsx("h1", { className: "text-3xl font-light", style: { color: 'var(--text-primary)' }, children: "App Switcher" }), _jsx("p", { className: "mt-1 text-sm", style: { color: 'var(--text-secondary)' }, children: "Switch between COW applications" })] }), _jsx(Button, { variant: "ghost", onClick: () => navigate(-1), children: "Back" })] }), _jsx("div", { className: "flex-1 overflow-y-auto px-8 py-6", children: loading ? (_jsx("div", { className: "flex items-center justify-center h-full", children: _jsx("p", { style: { color: 'var(--text-secondary)' }, children: "Loading app access..." }) })) : (_jsx(_Fragment, { children: ALL_APPS.filter(app => userAppAccess.has(app.id)).length > 0 ? (_jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6", children: ALL_APPS.filter(app => userAppAccess.has(app.id)).map((app) => {
                            const Icon = app.icon;
                            return (_jsxs("button", { onClick: () => handleAppClick(app), className: "relative p-6 rounded-2xl transition-all duration-200 hover:scale-105 cursor-pointer", style: {
                                    backgroundColor: 'var(--bg-elevated)',
                                    border: '1px solid var(--border-color)',
                                }, children: [_jsx("div", { className: "w-16 h-16 rounded-2xl flex items-center justify-center mb-4 mx-auto", style: {
                                            backgroundColor: `${app.color}20`,
                                        }, children: _jsx(Icon, { className: "w-8 h-8", style: { color: app.color } }) }), _jsx("h3", { className: "text-lg font-medium mb-1 text-center", style: { color: 'var(--text-primary)' }, children: app.name }), _jsx("p", { className: "text-sm text-center", style: { color: 'var(--text-secondary)' }, children: app.description })] }, app.id));
                        }) })) : (_jsx("div", { className: "flex flex-col items-center justify-center h-full", children: _jsxs("div", { style: { color: 'var(--text-secondary)' }, className: "text-center", children: [_jsx("p", { className: "text-lg font-medium mb-2", children: "No apps available" }), _jsx("p", { className: "text-sm", children: "Contact your administrator to request access to applications." })] }) })) })) })] }));
}
